/**
 * A place in the editor area that Go Back and Go Forward return to: a tab,
 * and where in it the person was.
 */
export interface EditorLocation {
    /** The tab's key: a file's path, or `view:` and the view's id. */
    key: string;
    /** The editor group that showed the tab; a closed group's id names no group. */
    group?: string;
    /** The cursor, 1-based, when the tab showed a file's text. */
    line?: number;
    column?: number;
    /** How far the tab was scrolled when it showed rendered Markdown or a file view. */
    scroll?: number;
}

/** A move within a file of at most this many lines changes the current location instead of adding one, as in VS Code. */
const NEAR_LINES = 10;
const LIMIT = 50;

/**
 * The editor locations the person went through, oldest first, and the one
 * they are at. Going back or forward moves along the list; a new location
 * drops the ones ahead of the current one, as a browser's history does.
 */
export class LocationHistory {
    #entries: EditorLocation[] = [];
    #index = -1;
    /** Set while a location is restored, so that opening its tab adds nothing. */
    #navigating = false;
    readonly #restore: (location: EditorLocation) => Promise<boolean>;
    readonly #current: () => EditorLocation | undefined;
    readonly #listeners = new Set<() => void>();

    /**
     * `restore` shows a location and resolves to false when its tab is gone;
     * `current` reads where the active tab is now.
     */
    constructor(restore: (location: EditorLocation) => Promise<boolean>, current: () => EditorLocation | undefined) {
        this.#restore = restore;
        this.#current = current;
    }

    get canGoBack(): boolean {
        return this.#index > 0;
    }

    get canGoForward(): boolean {
        return this.#index < this.#entries.length - 1;
    }

    onChange(listener: () => void): void {
        this.#listeners.add(listener);
    }

    /** Add a location the person went to, or move the current one there when it is near. */
    record(location: EditorLocation): void {
        if (this.#navigating) return;
        const at = this.#entries[this.#index];
        if (at !== undefined && near(at, location)) {
            this.#entries[this.#index] = location;
            return;
        }
        this.#entries.splice(this.#index + 1, Infinity, location);
        if (this.#entries.length > LIMIT) this.#entries.shift();
        this.#index = this.#entries.length - 1;
        this.#changed();
    }

    /** Keep where the person left the current location's tab, such as the scroll position. */
    update(location: EditorLocation): void {
        const at = this.#entries[this.#index];
        if (!this.#navigating && at !== undefined && sameTab(at, location)) this.#entries[this.#index] = location;
    }

    /** Forget every location, as after another project opens, and start at `location`. */
    clear(location = this.#current()): void {
        this.#entries = location === undefined ? [] : [location];
        this.#index = this.#entries.length - 1;
        this.#changed();
    }

    back(): Promise<void> {
        return this.#go(-1);
    }

    forward(): Promise<void> {
        return this.#go(1);
    }

    /** Show the location `step` places along, skipping and forgetting those whose tab is gone. */
    async #go(step: number): Promise<void> {
        if (this.#navigating) return;
        const current = this.#current();
        if (current !== undefined) this.update(current);
        this.#navigating = true;
        try {
            for (let i = this.#index + step; i >= 0 && i < this.#entries.length; i += step) {
                if (await this.#restore(this.#entries[i]!)) {
                    // The tab may show in another group now than when it was recorded.
                    this.#entries[i] = this.#current() ?? this.#entries[i]!;
                    this.#index = i;
                    return;
                }
                this.#entries.splice(i, 1);
                if (i < this.#index) this.#index--;
                if (step < 0) continue;
                i -= step;
            }
        } finally {
            this.#navigating = false;
            this.#changed();
        }
    }

    #changed(): void {
        for (const listener of this.#listeners) listener();
    }
}

/** A file or view is open in one group at a time, so a tab moved to another group is the same place. */
function sameTab(a: EditorLocation, b: EditorLocation): boolean {
    return a.key === b.key;
}

/** Whether `b` is the same place as `a` for the history: the same tab, and in a file's text a few lines away. */
function near(a: EditorLocation, b: EditorLocation): boolean {
    if (!sameTab(a, b)) return false;
    if (a.line === undefined || b.line === undefined) return a.line === b.line;
    return Math.abs(a.line - b.line) <= NEAR_LINES;
}
